import React, { useEffect, useState } from "react";
import {
  formatCurrency,
  rangePickerProps,
  rangePresets,
  useDebounce,
  INVOICE_WARNING_VISIBLE_ROLES,
  ROLE,
  formatToLocalDate,
  getAntdSortOrder,
  isInvoiceFeatureEnabled,
} from "../../../helpers/utils";
import { useTranslation } from "react-i18next";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { svg } from "../../../Assets/svg";
import {
  DatePicker,
  Dropdown,
  Menu,
  Select,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import isoWeek from "dayjs/plugin/isoWeek";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { GetQuotationListAPI } from "../../Redux/quotations";
import InfiniteScroll from "react-infinite-scroll-component";
import NoDataMessage from "../../Components/NoData";
import FullScreenLoader from "../../Components/FullScreenLoader";
import { useLocation, useNavigate } from "react-router-dom";
import { DeleteOrderAPI, GetOrdersFilterListAPI } from "../../Redux/orders";
import CreateOrderModal from "./Components/CreateOrderModal";
import ViewProfitDataModal from "./Components/Modals/ViewProfitDataModal";
import {
  manageOrderTypePermission,
  OrderStatusDropDownList,
  OrderStatusEnum,
  OrderStatusLabels,
  ORDER_SCROLL_KEY,
  ORDER_FILTER_KEY,
  ORDER_REFETCH_KEY,
} from "./orderTypes";
import CommonDeleteModal from "../../Components/Modals/CommonDeleteModal";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import {
  getDecryptedUserData,
  getDecryptedName,
} from "../../../helpers/bycrypt";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrderAPI,
  fetchOrdersQueryFn,
  toggleArchiveOrderAPI,
} from "./orderApis";
import ClearComponent from "../../Components/ClearComponent";
import { ModifyDeliveryAddressModal } from "../ClientsDistributors/Components/ModifyDeliveryAddressModal";
import ConfigureDrawingsModal from "../Quotations/Components/ConfigureDrawingsModal";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";
import { Capacitor } from "@capacitor/core";
import CustomerOnHoldWarningModal from "./Components/Modals/CustomerOnHoldWarningModal";
import AccountsApprovalModal from "./Components/Modals/AccountsApprovalModal";

dayjs.extend(utc);
dayjs.extend(isoWeek);

const { RangePicker }: any = DatePicker;

export const statusFindClass = (status: string) => {
  switch (status) {
    case OrderStatusEnum.ORDER_CREATED:
      return "statusDraft";
    case OrderStatusEnum.PRODUCTION_PLANNING:
      return "designApprovedStatus";
    case OrderStatusEnum.PRODUCTION_STARTED:
      return "approvedStatus";
    case OrderStatusEnum.IN_PRODUCTION:
      return "approvedStatus";
    case OrderStatusEnum.READY_FOR_DISPATCH:
      return "sentStatus";
    case OrderStatusEnum.DISPATCHED:
      return "savedStatus";
    case OrderStatusEnum.DELIVERED:
      return "deliveredStatus";
    case OrderStatusEnum.UNDER_INSTALLATION:
      return "underInstallationStatus";
    case OrderStatusEnum.INSTALLED:
      return "designPendingStatus";
    case OrderStatusEnum.CLOSED:
      return "closedStatus";
    default:
      return "statusDraft"; // Fallback
  }
};

// Intercept browser reloads and clear filter persistence so the page starts completely fresh
try {
  if (
    window.performance &&
    window.performance.navigation &&
    window.performance.navigation.type === 1
  ) {
    sessionStorage.removeItem(ORDER_SCROLL_KEY);
    sessionStorage.removeItem(ORDER_FILTER_KEY);
    sessionStorage.removeItem(ORDER_REFETCH_KEY);
  } else if (
    window.performance &&
    window.performance.getEntriesByType &&
    window.performance.getEntriesByType("navigation").length > 0
  ) {
    const navEntry: any = window.performance.getEntriesByType("navigation")[0];
    if (navEntry.type === "reload") {
      sessionStorage.removeItem(ORDER_SCROLL_KEY);
      sessionStorage.removeItem(ORDER_FILTER_KEY);
      sessionStorage.removeItem(ORDER_REFETCH_KEY);
    }
  }
} catch (e) {
  // ignore
}

const getPersistedFilters = () => {
  try {
    const pf = sessionStorage.getItem(ORDER_FILTER_KEY);
    return pf ? JSON.parse(pf) : null;
  } catch (e) {
    return null;
  }
};

const Orders = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();
  const { deleteOrderLoading, authLoading } = useAppSelector(
    (state: RootState) => state.loading,
  );

  const { CreatedByData, CompanyData } = useAppSelector(
    (state: RootState) => state.orders,
  );

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.ORDERS,
  );
  const userData = getDecryptedUserData();
  const [failedAddressData, setFailedAddressData] = useState<any>(null);
  const [drawingRequiredData, setDrawingRequiredData] = useState<any>(null);

  // ─── Filters (restored from sessionStorage on mount) ───────────────────────
  const _pf = getPersistedFilters();
  const user = getDecryptedUserData();
  const [search, setSearch] = useState<string>(_pf?.search ?? "");
  const debouncedSearch = useDebounce(search, 300);
  const [startDate, setStartDate] = useState<any>(_pf?.startDate ?? "");
  const [endDate, setEndDate] = useState<any>(_pf?.endDate ?? "");

  const [isDeleteId, setIsDeleteId] = useState<any>(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);

  const isArchiveView = location.pathname.includes("/archived");
  const [archiveFilter, setArchiveFilter] = useState<boolean>(
    isArchiveView || (_pf?.archiveFilter ?? false),
  );
  const [archiveActionData, setArchiveActionData] = useState<{
    id: string;
    type: "archive" | "unarchive";
  } | null>(null);
  const [isTogglingArchive, setIsTogglingArchive] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState<string>(
    location?.state?.dashboard_order_status || _pf?.selectedStatus || "",
  );
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<string>(
    _pf?.selectedCreatedBy ?? "",
  );
  const [selectedCompany, setSelectedCompany] = useState<string>(
    _pf?.selectedCompany ?? "",
  );
  const [selectedAccountsApproval, setSelectedAccountsApproval] =
    useState<string>(
      location?.state?.selectedAccountsApproval ??
        location?.state?.dashboard_accounts_approval ??
        _pf?.selectedAccountsApproval ??
        "",
    );

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(
    location?.state?.dashboard_order_status ||
      location?.state?.selectedAccountsApproval ||
      location?.state?.dashboard_accounts_approval
      ? true
      : _pf?.isFilterOpen ?? false,
  );

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "ascend" | "descend" | null;
  }>(_pf?.sorterState ?? { field: null, order: null });

  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);

  const [customerOnHoldModal, setCustomerOnHoldModal] = useState(false);
  const [customerOnHoldMessage, setCustomerOnHoldMessage] = useState("")
  const [profitData, setProfitData] = useState<any>(null);
  const [profitModal, setProfitModal] = useState(false);
  const [accountsApprovalModal, setAccountsApprovalModal] = useState(false);
  const [selectedOrderForApproval, setSelectedOrderForApproval] = useState<any>(null);

  // ─── Scroll & Query Logic ──────────────────────────────────────────────────
  const queryClient = useQueryClient();
  const scrollableDivRef = React.useRef<HTMLDivElement>(null);
  const scrollableDivId = "orders-scrollable-container";
  const [isMounting, setIsMounting] = useState(true);

  useEffect(() => {
    if (
      location?.state?.dashboard_order_status ||
      location?.state?.selectedAccountsApproval ||
      location?.state?.dashboard_accounts_approval
    ) {
      navigate(location.pathname, { replace: true });
    }
  }, [
    location?.state?.dashboard_order_status,
    location?.state?.selectedAccountsApproval,
    location?.state?.dashboard_accounts_approval,
    navigate,
    location.pathname,
  ]);

  // ─── Filter Persistence ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isMounting) {
      const filtersToSave = {
        search,
        startDate,
        endDate,
        selectedStatus,
        selectedCreatedBy,
        selectedCompany,
        selectedAccountsApproval,
        isFilterOpen,
        sorterState,
        archiveFilter,
      };
      sessionStorage.setItem(ORDER_FILTER_KEY, JSON.stringify(filtersToSave));
    }
  }, [
    search,
    startDate,
    endDate,
    selectedStatus,
    selectedCreatedBy,
    selectedCompany,
    selectedAccountsApproval,
    isFilterOpen,
    sorterState,
    archiveFilter,
    isMounting,
  ]);

  // ─── TanStack Query filters object ─────────────────────────────────────────
  const queryFilters = {
    search: debouncedSearch,
    start_date: startDate ? dayjs(startDate).startOf("day").toISOString() : "",
    end_date: endDate ? dayjs(endDate).endOf("day").toISOString() : "",
    sortBy: sorterState.field,
    sortOrder:
      sorterState?.order === "ascend"
        ? "asc"
        : sorterState?.order === "descend"
        ? "desc"
        : null,
    status: selectedStatus,
    customer_id: selectedCompany ? selectedCompany : [],
    created_by: selectedCreatedBy ? selectedCreatedBy : [],
    is_archived: archiveFilter.toString(),
    accounts_approved: selectedAccountsApproval,
  };

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["orders", queryFilters],
    queryFn: ({ pageParam = 1, signal }: any) =>
      fetchOrdersQueryFn(
        {
          page: pageParam as number,
          limit: 25,
          ...queryFilters,
        },
        signal,
      ),
    getNextPageParam: (lastPage: any, allPages: any) => {
      const fetched = allPages.reduce(
        (acc: number, p: any) => acc + (p?.orders?.length || 0),
        0,
      );
      const total = lastPage?.pagination?.total || 0;
      return fetched < total ? allPages.length + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    initialPageParam: 1,
  });

  const orderListData = React.useMemo(() => {
    if (!queryData?.pages) return [];
    return queryData.pages.flatMap((page: any) => page.orders || []);
  }, [queryData]);

  const totalCount = queryData?.pages[0]?.pagination?.total || 0;

  // Refetch if returning after edit
  useEffect(() => {
    if (sessionStorage.getItem(ORDER_REFETCH_KEY) === "1") {
      sessionStorage.removeItem(ORDER_REFETCH_KEY);
      setTimeout(() => {
        sessionStorage.removeItem(ORDER_SCROLL_KEY);
      }, 500);
      queryClient.removeQueries({ queryKey: ["orders"] });
    }
  }, [queryClient]);

  // ─── Reset scroll flag & scroll to top when filters change ─────────────────
  const isFirstMountForFilters = React.useRef(true);
  useEffect(() => {
    if (isFirstMountForFilters.current) {
      isFirstMountForFilters.current = false;
      return;
    }
    scrollRestoredRef.current = false;
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTop = 0;
    }

    // Clear all inactive order caches so revisiting a previous filter triggers a fresh Page 1 call
    queryClient.removeQueries({ queryKey: ["orders"], type: "inactive" });
  }, [
    debouncedSearch,
    selectedStatus,
    startDate,
    endDate,
    selectedCompany,
    selectedCreatedBy,
    sorterState.field,
    sorterState.order,
  ]);

  const scrollRestoredRef = React.useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsMounting(false), 20);
    return () => clearTimeout(timer);
  }, []);

  // Handle restoring scroll position EXACTLY ONCE after data populates
  React.useLayoutEffect(() => {
    if (
      !scrollRestoredRef.current &&
      orderListData?.length > 0 &&
      !isLoading &&
      !isMounting
    ) {
      const savedScroll = sessionStorage.getItem(ORDER_SCROLL_KEY);
      if (savedScroll) {
        const parent = scrollableDivRef.current;
        const target =
          parent?.querySelector(".infinite-scroll-component") || parent;
        // const target = scrollableDivRef.current;
        if (target) {
          target.scrollTop = parseInt(savedScroll, 10);
          requestAnimationFrame(() => {
            setTimeout(() => {
              target.dispatchEvent(new Event("scroll", { bubbles: true }));
            }, 100);
          });
        }
        sessionStorage.removeItem(ORDER_SCROLL_KEY);
      }
      scrollRestoredRef.current = true;
    }
  }, [isLoading, isMounting, orderListData]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    dispatch(GetOrdersFilterListAPI());
  }, []);
  // Table Sorting Handler
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter.order) {
      setSorterState({ field: sorter.field, order: sorter.order }); // Store sorter state
    } else {
      setSorterState({ field: null, order: null });
    }
  };

  const getFilterCount = () => {
    let count = 0;
    if (selectedStatus) count++;
    if (selectedCompany) count++;
    if (selectedCreatedBy) count++;
    if (startDate && endDate) count++;
    if (archiveFilter) count++;
    return count;
  };

  // Toggle Filter Drawer

  const toggleFilter = () => {
    setIsFilterOpen((prevState) => !prevState);
  };

  // Search Bar & Date Handler
  const searchBarHandler = (e: any) => {
    setSearch(e.target.value);
  };

  const searchBarClean = () => {
    setSearch("");
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      const [start, end]: any = dates;
      setStartDate(start?.utc()?.toISOString());
      setEndDate(end?.utc()?.toISOString());
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        <Menu.Item
          key="1"
          onClick={() => {
            if (scrollableDivRef.current) {
              sessionStorage.setItem(
                ORDER_SCROLL_KEY,
                String(scrollableDivRef.current.scrollTop),
              );
            }
            navigate(`/orders/${data?._id}`);
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            View
          </div>
        </Menu.Item>

        <Menu.Item
          key="2"
          onClick={() => {
            if (scrollableDivRef.current) {
              sessionStorage.setItem(
                ORDER_SCROLL_KEY,
                String(scrollableDivRef.current.scrollTop),
              );
            }
            navigate(`/orders/${data._id}/audit-log`, {
              state: {
                backPath: "/orders",
              },
            });
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Audit Log
          </div>
        </Menu.Item>

        {[ROLE.ADMIN, ROLE.SUB_ADMIN].includes(userData?.role?.key) ? (
          <Menu.Item
            key="3"
            onClick={({ domEvent }: any) => {
              domEvent?.stopPropagation?.();
              setIsDeleteId(data?._id);
              setIsDeleteModal(true);
            }}
          >
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Delete
            </div>
          </Menu.Item>
        ) : (
          ""
        )}
        {USER_PERMISSIONS?.REMOVE ? (
          <Menu.Item
            key="4"
            onClick={({ domEvent }: any) => {
              domEvent?.stopPropagation?.();
              setArchiveActionData({
                id: data?._id,
                type: archiveFilter ? "unarchive" : "archive",
              });
            }}
          >
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              {archiveFilter ? "Unarchive" : "Archive"}
            </div>
          </Menu.Item>
        ) : (
          ""
        )}
      </Menu>
    );
  };

  const handleDeleteOrderData = () => {
    const payload = {
      id: isDeleteId,
    };
    const onDeleteSuceess = () => {
      queryClient.setQueryData(["orders", queryFilters], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            orders: (page.orders || []).filter(
              (o: any) => o._id !== isDeleteId,
            ),
            pagination: {
              ...(page.pagination || {}),
              total: Math.max(0, (page.pagination?.total || 1) - 1),
            },
          })),
        };
      });
      setIsDeleteId(null);
      setIsDeleteModal(false);
    };
    dispatch(DeleteOrderAPI(payload, onDeleteSuceess));
  };

  const handleArchiveConfirm = () => {
    if (!archiveActionData) return;
    setIsTogglingArchive(true);
    dispatch(
      toggleArchiveOrderAPI({
        order_id: archiveActionData.id,
        is_archived: archiveActionData.type === "archive",
        onSuccess: () => {
          sessionStorage.removeItem(ORDER_SCROLL_KEY);
          sessionStorage.removeItem(ORDER_FILTER_KEY);
          sessionStorage.setItem(ORDER_REFETCH_KEY, "1");
          setIsTogglingArchive(false);
          setArchiveActionData(null);
          queryClient.removeQueries({ queryKey: ["orders"] });
        },
        onError: () => {
          setIsTogglingArchive(false);
        },
      }),
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: "",
      dataIndex: "is_sage_synced",
      key: "is_sage_synced",
      align: "center",
      render: (is_sage_synced: any, record: any) => {
        return (
          <Tooltip title={is_sage_synced ? "Data Synced" : "Data Not Synced"}>
            <span className="d-flex align-items-center justify-content-center svg-20 cursor-pointer">
              {is_sage_synced ? svg?.sageIconSync : svg?.sageNonSyncIcon}
            </span>
          </Tooltip>
        );
      },
      width: 50,
    },
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
      sortOrder: sorterState.field === "order_id" ? sorterState.order : null,
      width: 110,
      render: (order_id: any, record: any) => (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span>{order_id ? `${order_id}` : "-"}</span>
          {record?.is_2bs_order && (
            <Tag
              color="blue"
              className="fs-8 py-0 px-2 fw-semibold m-0"
              style={{ borderRadius: "4px", lineHeight: "18px" }}
            >
              2BS Order
            </Tag>
          )}
          {record?.is_eccomerce_order && (
            <Tag
              color="orange"
              className="fs-8 py-0 px-2 fw-semibold m-0"
              style={{ borderRadius: "4px", lineHeight: "18px" }}
            >
              Ecommerce Order
            </Tag>
          )}
          {record?.is_eccomerce_order && record?.order_placed_type && (
            <Tag
              color={
                record?.order_placed_type === "takeaway" ? "purple" : "cyan"
              }
              className="fs-8 py-0 px-2 fw-semibold m-0 text-capitalize"
              style={{ borderRadius: "4px", lineHeight: "18px" }}
            >
              {record?.order_placed_type}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Quote ID",
      dataIndex: "display_quote_id",
      key: "display_quote_id",
      sorter: true,
      sortOrder:
        sorterState.field === "display_quote_id" ? sorterState.order : null,
      width: 110,
      render: (display_quote_id: any, record: any) =>
        display_quote_id ? (
          <div
            className="text-decoration-underline cursor-pointer text-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              if (scrollableDivRef.current) {
                sessionStorage.setItem(
                  ORDER_SCROLL_KEY,
                  String(scrollableDivRef.current.scrollTop),
                );
              }
              navigate(
                `/quotations/${record?.obj_quote_id}?tab=customer-details`,
                { state: { backPath: `/orders` } },
              );
            }}
          >
            {display_quote_id}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Company Name",
      dataIndex: "customer_snapshot.company_name",
      key: "customer_snapshot.company_name",
      sorter: true,
      sortOrder:
        sorterState.field === "customer_snapshot.company_name"
          ? sorterState.order
          : null,
      width: 150,
      render: (customer_snapshot: any, record: any) => (
        <div
          style={{
            wordBreak: "keep-all",
            overflowWrap: "normal",
            whiteSpace: "normal",
          }}
        >
          {record?.customer_snapshot?.company_name || "-"}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "grand_total",
      key: "grand_total",
      sorter: true,
      sortOrder: sorterState.field === "grand_total" ? sorterState.order : null, // Keep arrow selected
      width: 105,
      render: (grand_total: any, record: any) => (
        <span className="text-nowrap">
          {formatCurrency(
            Number(grand_total) || 0,
            record?.currency,
            record?.conversion_rate,
          )}
        </span>
      ),
    },
    ...([ROLE.ADMIN, ROLE.SUB_ADMIN].includes(user?.role?.key)
      ? [
          {
            title: "Estimated Profit",
            dataIndex: "estimated_profit",
            key: "estimated_profit",
            sorter: true,
            sortOrder:
              sorterState.field === "estimated_profit"
                ? sorterState.order
                : null,
            width: 130,
            render: (value: any, record: any) => {
              const profit_data = record?.profit_data;
              const profit = profit_data?.estimated_profit || 0;
              return (
                <div
                  className="cursor-pointer text-nowrap"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfitData({
                      ...profit_data,
                      display_quote_id: record?.display_quote_id,
                      vat_percentages: record?.vat_percentage,
                      conversion_rate: record?.conversion_rate,
                      currency: record?.currency,
                    });
                    setProfitModal(true);
                  }}
                >
                  {profit > 0 ? (
                    <span className="text-success fw-semibold">
                      {formatCurrency(
                        Number(profit) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : profit < 0 ? (
                    <span className="text-danger fw-semibold">
                      {formatCurrency(
                        Number(Math.abs(profit)) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : (
                    formatCurrency(
                      Number(0) || 0,
                      record?.currency,
                      record?.conversion_rate,
                    )
                  )}
                </div>
              );
            },
          },
          {
            title: "Actual Profit",
            dataIndex: "total_profit",
            key: "total_profit",
            sorter: true,
            sortOrder:
              sorterState.field === "total_profit" ? sorterState.order : null,
            width: 115,
            render: (value: any, record: any) => {
              const profit_data = record?.profit_data;
              const profit = profit_data?.total_profit || 0;
              return !profit_data?.is_order_created ? (
                <Tooltip
                  title={
                    "The actual profit calculation will be shown after the order is invoiced."
                  }
                >
                  <div className="d-flex">-</div>
                </Tooltip>
              ) : (
                <div
                  className="cursor-pointer text-nowrap"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfitData({
                      ...profit_data,
                      display_order_id: record?.order_id,
                      vat_percentages: record?.vat_percentage,
                      conversion_rate: record?.conversion_rate,
                      currency: record?.currency,
                    });
                    setProfitModal(true);
                  }}
                >
                  {profit > 0 ? (
                    <span className="text-success fw-semibold">
                      {formatCurrency(
                        Number(profit) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : profit < 0 ? (
                    <span className="text-danger fw-semibold">
                      {formatCurrency(
                        Number(Math.abs(profit)) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : (
                    formatCurrency(
                      Number(0) || 0,
                      record?.currency,
                      record?.conversion_rate,
                    )
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: "Next Payment Stage",
      dataIndex: "payment_milestones",
      key: "payment_milestones",
      width: 100,
      render: (payment_milestones: any, record: any) => {
        const findUnPaidMilestone = payment_milestones?.find(
          (elem: any) => elem?.paid_status === "pending" || !elem?.paid_status,
        );
        // const finalAmount = `${findUnPaidMilestone?.milestone_name} - ${
        //   findUnPaidMilestone?.value_base === "percentage"
        //     ? `${findUnPaidMilestone?.value}%`
        //     : formatCurrency(findUnPaidMilestone?.value, record?.currency, record?.conversion_rate)
        // }`;
        const finalAmount = `${findUnPaidMilestone?.milestone_name}`;
        return payment_milestones?.length > 0 ? (
          findUnPaidMilestone ? (
            finalAmount
          ) : (
            <span className="text-success fw-semibold">Paid</span>
          )
        ) : (
          "-"
        );
      },
    },
    {
      title: "Estimate / Actual Lead Time",
      dataIndex: "estimated_lead_time",
      key: "estimated_lead_time",
      width: 115,
      render: (_: any, record: any) => {
        const orderStatus = record?.order_status;
        const estimatedLeadTimeDays = record?.estimated_lead_time;
        const rawActualLeadTime = record?.actual_lead_time;

        if (estimatedLeadTimeDays == null && rawActualLeadTime == null) {
          return <span>-</span>;
        }

        const estimatedLeadTimeWeeks =
          estimatedLeadTimeDays != null
            ? Number((estimatedLeadTimeDays / 7).toFixed(1))
            : null;

        const actualLeadTimeWeeks =
          rawActualLeadTime != null
            ? Number((rawActualLeadTime / 7).toFixed(1))
            : 0;

        const isLeadTimeDispatched =
          [
            OrderStatusEnum.READY_FOR_DISPATCH,
            OrderStatusEnum.DISPATCHED,
            OrderStatusEnum.DELIVERED,
            OrderStatusEnum.UNDER_INSTALLATION,
            OrderStatusEnum.INSTALLED,
            OrderStatusEnum.CLOSED,
          ].includes(orderStatus) && rawActualLeadTime != null;

        if (estimatedLeadTimeWeeks == null) {
          return <span>-</span>;
        }

        if (!isLeadTimeDispatched) {
          return (
            <span className="fw-semibold text-primary">
              {estimatedLeadTimeWeeks.toFixed(1)}{" "}
              {estimatedLeadTimeWeeks === 1 ? "Week" : "Weeks"}
            </span>
          );
        }

        const diffWeeks = Number(
          (actualLeadTimeWeeks - estimatedLeadTimeWeeks).toFixed(1),
        );
        const leadTimeExceeded = diffWeeks > 0;

        return (
          <div className="d-flex align-items-center gap-1 flex-wrap">
            <span className="fw-semibold text-primary">
              {estimatedLeadTimeWeeks.toFixed(1)} /{" "}
              {actualLeadTimeWeeks.toFixed(1)} Weeks
            </span>
            <span
              className={`fw-bold ${
                leadTimeExceeded ? "text-danger" : "text-success"
              }`}
            >
              ({leadTimeExceeded ? `+${diffWeeks}` : `${diffWeeks}`}{" "}
              {Math.abs(diffWeeks) === 1 ? "Week" : "Weeks"})
            </span>
          </div>
        );
      },
    },
    {
      title: "Production Slot",
      dataIndex: "production_slot",
      key: "production_slot",
      sorter: true,
      sortOrder:
        sorterState.field === "production_slot" ? sorterState.order : null,
      width: 110,
      render: (_: any, record: any) => {
        const rawSlot =
          record?.production_slot ?? record?.planning_details?.production_slot;

        if (!rawSlot) {
          return <span>-</span>;
        }

        const startDate =
          record?.production_start_date ??
          record?.planning_details?.production_start_date;
        const weekNum =
          startDate && dayjs(startDate).isValid()
            ? dayjs(startDate).isoWeek()
            : null;

        const slotStr = String(rawSlot).trim();

        if (slotStr.toUpperCase().includes("FAST")) {
          return (
            <div className="d-flex flex-column align-items-start gap-1">
              {weekNum && (
                <span className="fw-semibold text-dark">
                  {weekNum}
                </span>
              )}
              <span
                className="fw-semibold m-0 badge bg-danger fw-bold"
                style={{
                  fontSize: "10px",
                  padding: "0 4px",
                  lineHeight: "16px",
                  borderRadius: "4px",
                }}
              >
                FAST-TRACK
              </span>
            </div>
          );
        }

        // If slot already contains week (e.g. "45.04" or "45.48")
        if (slotStr.includes(".")) {
          return (
            <span className="fw-semibold text-dark text-nowrap">{slotStr}</span>
          );
        }

        const slotNumStr =
          slotStr.length === 1 ? slotStr.padStart(2, "0") : slotStr;

        const displayValue = weekNum
          ? `${weekNum}.${slotNumStr}`
          : slotNumStr;

        return (
          <span className="fw-semibold text-dark text-nowrap">
            {displayValue || "-"}
          </span>
        );
      },
    },
    ...(archiveFilter
      ? []
      : [
          {
            title: "Created By",
            dataIndex: "order_created_by",
            key: "order_created_by",
            sorter: true,
            sortOrder:
              sorterState.field === "order_created_by"
                ? sorterState.order
                : null, // Keep arrow selected
            width: 140,
            render: (order_created_by: any, record: any) => {
              const displayName = order_created_by
                ? `${order_created_by?.first_name || ""} ${order_created_by?.last_name || ""}`.trim()
                : "";
              const dateStr = record?.created_at
                ? formatToLocalDate(record.created_at, true)
                : "";

              return (
                <div>
                  <div className="text-nowrap">{displayName || "-"}</div>
                  {dateStr && (
                    <div
                      className="text-muted text-nowrap"
                      style={{ fontSize: "12px" }}
                    >
                      {dateStr}
                    </div>
                  )}
                </div>
              );
            },
          },
        ]),
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      sorter: true,
      sortOrder:
        sorterState.field === "order_status" ? sorterState.order : null, // Keep arrow selected
      width: 155,
      render: (order_status: any, record: any) => {
        const statusLabel =
          OrderStatusLabels[order_status as OrderStatusEnum] || order_status;

        const isFinalInvoicePending = !!record?.enable_to_create_final_invoice;

        const canApprove = [
          ROLE.ADMIN,
          ROLE.SUB_ADMIN,
          ROLE.ACCOUNTS,
          ROLE.CREDIT_CONTROLLER,
        ].includes(user?.role?.key);

        return (
          <div className="d-flex flex-column align-items-start gap-1">
            <div className="d-flex align-items-center gap-2">
              <span
                className={
                  "d-inline-flex align-items-center billStatus " +
                  statusFindClass(order_status)
                }
              >
                {svg.dotIcon}
                {statusLabel == "Delivered" && !record?.ProductionPlanning
                  ? "Ready For Installation"
                  : statusLabel}
              </span>
              {isInvoiceFeatureEnabled &&
                INVOICE_WARNING_VISIBLE_ROLES.includes(userData?.role?.key) &&
                record?.invoice_warnings &&
                record?.invoice_warnings.length > 0 && (
                  <Tooltip
                    title={
                      <div className="d-flex flex-column gap-1">
                        {record.invoice_warnings.map((w: string, i: number) => (
                          <span key={i}>- {w}</span>
                        ))}
                      </div>
                    }
                    placement="topRight"
                  >
                    <span className="text-warning svg-20 d-flex align-items-center cursor-pointer">
                      <ExclamationCircleFilled />
                    </span>
                  </Tooltip>
                )}
            </div>
            {isInvoiceFeatureEnabled &&
              isFinalInvoicePending &&
              record?._id && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    record?._id &&
                      navigate(`/invoices/create-invoice/${record?._id}/final`);
                  }}
                  className="cursor-pointer"
                  style={{
                    color: "red",
                    fontWeight: "normal",
                    fontSize: "12px",
                  }}
                >
                  Final Invoice Pending -{" "}
                  <span style={{ textDecoration: "underline" }}>Create</span>
                </span>
              )}
            {record?.accounts_approved != null && (
              record.accounts_approved ? (
                <span className="accountsApprovalBadge approved">
                  {svg.dotIcon}
                  Accounts Approved
                </span>
              ) : (
                <span
                  role={canApprove ? "button" : undefined}
                  tabIndex={canApprove ? 0 : undefined}
                  className={`accountsApprovalBadge needed ${!canApprove ? "non-clickable" : ""}`}
                  style={!canApprove ? { cursor: "default", pointerEvents: "none" } : undefined}
                  onClick={
                    canApprove
                      ? (e) => {
                          e.stopPropagation();
                          setSelectedOrderForApproval(record);
                          setAccountsApprovalModal(true);
                        }
                      : undefined
                  }
                >
                  {svg.dotIcon}
                  <span className="text-start">
                    Accounts Approval
                    <br />
                    Needed
                  </span>
                </span>
              )
            )}
          </div>
        );
      },
    },
    ...(archiveFilter
      ? [
          {
            title: "Archived By",
            dataIndex: "archived_by",
            key: "archived_by",
            sorter: true,
            sortOrder: getAntdSortOrder("archived_by", sorterState as any),
            width: 150,
            render: (user: any) =>
              user
                ? `${getDecryptedName(user)} (${user?.role?.name || ""})`
                : "-",
          },
          {
            title: "Archived At",
            dataIndex: "archived_at",
            key: "archived_at",
            sorter: true,
            sortOrder: getAntdSortOrder("archived_at", sorterState as any),
            width: 150,
            render: (archivedAt: any) =>
              archivedAt ? formatToLocalDate(archivedAt, true) : "-",
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      width: 70,
      align: "center",
      render: (_, record) => (
        <div
          className="d-flex justify-content-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-28 svg-outline-primary">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (
    isArchiveView &&
    ![ROLE.ADMIN, ROLE.SUB_ADMIN].includes(user?.role?.key)
  ) {
    return <NoPermissionPage />;
  }

  return USER_PERMISSIONS?.READ ? (
    <>
      <div
        className={`page-content h-100 px-40 py-40 ${isMobile ? "pb-0" : ""}`}
      >
        <div className="mb-3 d-flex flex-column gap-md-2 gap-0">
          <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
            <h2 className="text-black fw-bolder page-title mb-0 order-sm-1 order-1 d-flex align-items-center gap-2">
              {archiveFilter ? "Archived Orders" : t("orders.orders")}{" "}
              <span>
                {isLoading ? "" : totalCount ? `(${totalCount})` : ""}
              </span>
            </h2>
            <div
              className={`pageSearch mw-100 position-relative d-inline-block ms-auto order-sm-2 order-3`}
            >
              <input
                type="text"
                value={search}
                className="form-control form-control-lg"
                onChange={searchBarHandler}
                placeholder="Search"
              />
              {search?.length ? (
                <span className="cursor-pointer" onClick={searchBarClean}>
                  {svg?.searchClear}
                </span>
              ) : (
                <span>{svg?.search}</span>
              )}
            </div>

            {!archiveFilter && USER_PERMISSIONS?.ADD ? (
              <div className="d-md-flex justify-content-end align-items-center order-md-3 order-2 ms-sm-0 ms-auto">
                <button
                  className="btn btn-lg btn-primary mob-btn"
                  onClick={() => {
                    setIsCreateOrderModalOpen(true);
                  }}
                >
                  + CREATE
                </button>
              </div>
            ) : (
              ""
            )}
            <div className="d-flex gap-md-3 gap-2 order-sm-3 order-2">
              <div
                onClick={toggleFilter}
                role="button"
                aria-expanded={isFilterOpen}
                className="filterButton position-relative ms-sm-0 ms-auto"
              >
                {svg?.filterIcon}
                {getFilterCount() > 0 && (
                  <span className="position-absolute badge customBadgeFont rounded-pill bg-danger">
                    {getFilterCount()}
                  </span>
                )}
              </div>
              <NotificationButton />
            </div>
          </div>
          <div
            className={`filter-transition-container overflow-hidden ${
              isFilterOpen ? "is-open" : ""
            }`}
          >
            <div className="d-flex flex-wrap align-items-end bg-white p-2 rounded-3 mt-2 position-relative">
              <label className="filterTitle fw-bold fs-5 position-absolute d-flex align-items-center">
                Filters
              </label>
              <div className="row gx-md-3 gx-2 gy-sm-3 gy-2">
                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Company
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      defaultValue={""}
                      className="form-select form-select-lg"
                      onChange={(value) => {
                        setSelectedCompany(value);
                      }}
                    >
                      <Select.Option value={""}>All</Select.Option>
                      {CompanyData?.map((elem: any) => {
                        return (
                          <Select.Option value={elem?._id}>
                            {elem?.company_name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>
                <div className="col-lg-auto col-md-4 col-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Status
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      value={selectedStatus}
                      className="form-select form-select-lg"
                      onChange={(value) => {
                        setSelectedStatus(value);
                      }}
                    >
                      <Select.Option value={""}>All</Select.Option>
                      {OrderStatusDropDownList?.map((elem: any) => {
                        return (
                          <Select.Option value={elem?.value}>
                            {elem?.key}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>
                <div className="col-lg-auto col-md-4 col-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Created By
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      defaultValue={""}
                      className="form-select form-select-lg"
                      onChange={(value) => {
                        setSelectedCreatedBy(value);
                      }}
                    >
                      <Select.Option value={""}>All</Select.Option>
                      {CreatedByData?.map((elem: any) => {
                        return (
                          <Select.Option value={elem?._id}>
                            {elem?.name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>
                {/* Archive Filter */}
                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Archive
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      // style={{ width: "180px" }}
                      value={archiveFilter}
                      onChange={setArchiveFilter}
                      className="form-select form-select-lg"
                    >
                      <Select.Option value={false}>Unarchived</Select.Option>
                      <Select.Option value={true}>Archived</Select.Option>
                    </Select>
                  </div>
                </div>
                {/* Accounts Approval Filter */}
                {[ROLE.ADMIN, ROLE.SUB_ADMIN, ROLE.ACCOUNTS].includes(
                  user?.role?.key,
                ) && (
                  <div className="col-lg-auto col-md-4 col-sm-6">
                    <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                      Accounts Approval
                    </label>
                    <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                      <Select
                        value={selectedAccountsApproval}
                        className="form-select form-select-lg"
                        onChange={(value) => {
                          setSelectedAccountsApproval(value);
                        }}
                      >
                        <Select.Option value={""}>All</Select.Option>
                        <Select.Option value={"false"}>
                          Approval Needed
                        </Select.Option>
                        <Select.Option value={"true"}>
                          Approved
                        </Select.Option>
                      </Select>
                    </div>
                  </div>
                )}
                {/* Date Filter */}
                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Date Range
                  </label>
                  <div
                    className={`dateSelectFilter RangeDatePicker border-s mw-100 d-inline-block mb-sm-0 mb-2 ${
                      startDate === "" && endDate === ""
                        ? "not-active"
                        : "is-active"
                    } p-0`}
                  >
                    <RangePicker
                      presets={rangePresets}
                      onChange={onRangeChange}
                      {...rangePickerProps}
                      value={
                        startDate && endDate
                          ? [dayjs(startDate), dayjs(endDate)]
                          : null
                      }
                      inputReadOnly={Capacitor.isNativePlatform()}
                    />
                  </div>
                </div>
                {selectedCompany ||
                startDate ||
                endDate ||
                selectedCreatedBy ||
                selectedStatus ||
                selectedAccountsApproval ||
                archiveFilter ||
                search ? (
                  <ClearComponent
                    handleClick={() => {
                      setSelectedCompany("");
                      setStartDate("");
                      setEndDate("");
                      setSelectedCreatedBy("");
                      setSelectedStatus("");
                      setSelectedAccountsApproval("");
                      setArchiveFilter(false);
                      setSearch("");
                      setIsFilterOpen(false);
                    }}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={`card page-table-card page-table-device border-0 shadow rounded-4 flex-fill ${
            isMobile && isFilterOpen ? "" : "overflow-y-auto"
          }`}
        >
          <div
            className="card-body overflow-y-auto"
            id={scrollableDivId}
            ref={scrollableDivRef}
          >
            {isLoading || isMounting ? (
              <FullScreenLoader />
            ) : orderListData?.length === 0 ? (
              <NoDataMessage
                icon={svg?.noOrderIcon}
                text={
                  archiveFilter
                    ? "You do not have any archived orders yet."
                    : "You do not have any orders yet."
                }
              />
            ) : (
              <>
                <InfiniteScroll
                  dataLength={orderListData.length}
                  next={handleLoadMore}
                  hasMore={!!hasNextPage}
                  loader={
                    isFetchingNextPage && (
                      <div className="text-center p-2">
                        <Spin />
                      </div>
                    )
                  }
                  scrollableTarget={scrollableDivId}
                  height={"100%"}
                  style={{ overflow: "auto", paddingBottom: isMobile ? 16 : 0 }}
                >
                  {!isMobile ? (
                    /* --- DESKTOP VIEW: Table element only --- */
                    <Table
                      dataSource={orderListData}
                      scroll={{ x: "max-content" }}
                      pagination={false}
                      columns={columns}
                      onChange={handleTableChange}
                      rowKey={(record) => record._id}
                      className="custom-table ordersTable orderListPageTable cursor-pointer"
                      bordered
                      onRow={(record) => ({
                        onClick: () => {
                          const parent = scrollableDivRef?.current;
                          const target =
                            parent?.querySelector(
                              ".infinite-scroll-component",
                            ) || parent;
                          if (target) {
                            sessionStorage.setItem(
                              ORDER_SCROLL_KEY,
                              String(target.scrollTop),
                            );
                          }
                          navigate(`/orders/${record?._id}`);
                        },
                      })}
                    />
                  ) : (
                    /* --- MOBILE VIEW: Card Layout as per Quotations style --- */
                    <div className="d-flex flex-column gap-3 px-3">
                      {orderListData.map((record: any) => {
                        const isFinalInvoicePending =
                          !!record?.enable_to_create_final_invoice;

                        return (
                          <div
                            key={record._id}
                            className="card mobile-table-card mobile-font-s rounded-4 p-3"
                            onClick={() => {
                              if (scrollableDivRef.current) {
                                sessionStorage.setItem(
                                  ORDER_SCROLL_KEY,
                                  String(scrollableDivRef.current.scrollTop),
                                );
                              }
                              navigate(`/orders/${record?._id}`);
                            }}
                          >
                            {/* Card Header: Order ID & Status/Action */}
                            <div className="d-flex flex-column gap-1 mb-2">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span className="fw-bold fs-5">
                                    #{record.order_id || "-"}
                                  </span>
                                  {record?.is_2bs_order && (
                                    <Tag
                                      color="blue"
                                      className="fs-8 py-0 px-2 fw-semibold m-0"
                                      style={{
                                        borderRadius: "4px",
                                        lineHeight: "18px",
                                      }}
                                    >
                                      2BS Order
                                    </Tag>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    className={`billStatus ${statusFindClass(
                                      record.order_status,
                                    )} d-inline-flex align-items-center`}
                                  >
                                    {svg.dotIcon}
                                    {OrderStatusLabels[
                                      record.order_status as OrderStatusEnum
                                    ] || record.order_status}
                                  </span>
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <Dropdown
                                      overlay={() => MoreOptionsMenu(record)}
                                      trigger={["click"]}
                                      placement="bottomRight"
                                    >
                                      <span className="cursor-pointer">
                                        {svg?.tableMobileOption}
                                      </span>
                                    </Dropdown>
                                  </div>
                                </div>
                              </div>
                              {isInvoiceFeatureEnabled &&
                                isFinalInvoicePending &&
                                record?._id && (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      record?._id &&
                                        navigate(
                                          `/invoices/create-invoice/${record?._id}/final`,
                                        );
                                    }}
                                    className="cursor-pointer d-block text-end mt-1"
                                    style={{
                                      color: "red",
                                      fontWeight: "normal",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Final Invoice Pending -{" "}
                                    <span
                                      style={{ textDecoration: "underline" }}
                                    >
                                      Create
                                    </span>
                                  </span>
                                )}
                            </div>

                            {/* Subtitle: Company Name */}
                            <h6 className="fw-bold fs-5 mb-2">
                              {record?.customer_snapshot?.company_name || "-"}
                            </h6>

                            {/* Body: Order Details */}
                            <div className="mobile-card-details fs-6 fs-6-mob">
                              <div className="row gy-1">
                                <div className="col-12 col-md-6">
                                  <div className="row">
                                    <div className="col-sm-3 col-4 text-secondary fw-medium">
                                      Quote ID:
                                    </div>
                                    <div className="col-sm-9 col-8">
                                      <span
                                        className="text-decoration-underline text-primary cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (scrollableDivRef.current) {
                                            sessionStorage.setItem(
                                              ORDER_SCROLL_KEY,
                                              String(
                                                scrollableDivRef.current
                                                  .scrollTop,
                                              ),
                                            );
                                          }
                                          navigate(
                                            `/quotations/${record?.obj_quote_id}?tab=customer-details`,
                                            { state: { backPath: `/orders` } },
                                          );
                                        }}
                                      >
                                        {record.display_quote_id || "-"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-md-6">
                                  <div className="row">
                                    <div className="col-sm-3 col-4 text-secondary fw-medium">
                                      Est.Profit:
                                    </div>
                                    <div className="col-sm-9 col-8">
                                      <div
                                        className="cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setProfitData({
                                            ...record?.profit_data,
                                            display_quote_id:
                                              record?.display_quote_id,
                                            vat_percentages:
                                              record?.vat_percentage,
                                            conversion_rate:
                                              record?.conversion_rate,
                                            currency: record?.currency,
                                          });
                                          setProfitModal(true);
                                        }}
                                      >
                                        {record?.profit_data?.estimated_profit >
                                        0 ? (
                                          <span className="text-success fw-semibold">
                                            {formatCurrency(
                                              Number(
                                                record?.profit_data
                                                  ?.estimated_profit,
                                              ) || 0,
                                              record?.currency,
                                              record?.conversion_rate,
                                            )}
                                          </span>
                                        ) : record?.profit_data
                                            ?.estimated_profit < 0 ? (
                                          <span className="text-danger fw-semibold">
                                            {formatCurrency(
                                              Number(
                                                Math.abs(
                                                  record?.profit_data
                                                    ?.estimated_profit,
                                                ),
                                              ) || 0,
                                              record?.currency,
                                              record?.conversion_rate,
                                            )}
                                          </span>
                                        ) : (
                                          formatCurrency(
                                            Number(0) || 0,
                                            record?.currency,
                                            record?.conversion_rate,
                                          )
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-md-6">
                                  <div className="row">
                                    <div className="col-sm-3 col-4 text-secondary fw-medium">
                                      Actual Profit:
                                    </div>
                                    <div className="col-sm-9 col-8">
                                      {!record?.profit_data
                                        ?.is_order_created ? (
                                        <Tooltip
                                          title={
                                            "The actual profit calculation will be shown after the order is invoiced."
                                          }
                                        >
                                          <div className="d-flex justify-content-start">
                                            -
                                          </div>
                                        </Tooltip>
                                      ) : (
                                        <div
                                          className="cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setProfitData({
                                              ...record?.profit_data,
                                              display_order_id:
                                                record?.order_id,
                                              vat_percentages:
                                                record?.vat_percentage,
                                              conversion_rate:
                                                record?.conversion_rate,
                                              currency: record?.currency,
                                            });
                                            setProfitModal(true);
                                          }}
                                        >
                                          {record?.profit_data?.total_profit >
                                          0 ? (
                                            <span className="text-success fw-semibold">
                                              {formatCurrency(
                                                Number(
                                                  record?.profit_data
                                                    ?.total_profit,
                                                ) || 0,
                                                record?.currency,
                                                record?.conversion_rate,
                                              )}
                                            </span>
                                          ) : record?.profit_data
                                              ?.total_profit < 0 ? (
                                            <span className="text-danger fw-semibold">
                                              {formatCurrency(
                                                Number(
                                                  Math.abs(
                                                    record?.profit_data
                                                      ?.total_profit,
                                                  ),
                                                ) || 0,
                                                record?.currency,
                                                record?.conversion_rate,
                                              )}
                                            </span>
                                          ) : (
                                            formatCurrency(
                                              Number(0) || 0,
                                              record?.currency,
                                              record?.conversion_rate,
                                            )
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-md-6">
                                  <div className="row">
                                    <div className="col-sm-3 col-4 text-secondary fw-medium">
                                      Next Stage:
                                    </div>
                                    <div className="col-sm-9 col-8">
                                      {(() => {
                                        const payment_milestones =
                                          record.payment_milestones;
                                        const findUnPaidMilestone =
                                          payment_milestones?.find(
                                            (elem: any) =>
                                              elem?.paid_status === "pending" ||
                                              !elem?.paid_status,
                                          );
                                        return payment_milestones?.length >
                                          0 ? (
                                          findUnPaidMilestone ? (
                                            findUnPaidMilestone?.milestone_name
                                          ) : (
                                            <span className="text-success fw-semibold">
                                              Paid
                                            </span>
                                          )
                                        ) : (
                                          "-"
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                                {archiveFilter ? (
                                  <>
                                    <div className="col-12 col-md-6">
                                      <div className="row">
                                        <div className="col-sm-3 col-4 text-secondary fw-medium">
                                          Archived by:
                                        </div>
                                        <div className="col-sm-9 col-8">
                                          {record?.archived_by
                                            ? `${getDecryptedName(
                                                record?.archived_by,
                                              )} (${
                                                record?.archived_by?.role
                                                  ?.name || ""
                                              })`
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                      <div className="row">
                                        <div className="col-sm-3 col-4 text-secondary fw-medium">
                                          Archived on:
                                        </div>
                                        <div className="col-sm-9 col-8">
                                          {record?.archived_at
                                            ? dayjs(record?.archived_at).format(
                                                "DD MMM YYYY, hh:mm A",
                                              )
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {" "}
                                    <div className="col-12 col-md-6">
                                      <div className="row">
                                        <div className="col-sm-3 col-4 text-secondary fw-medium">
                                          Created by:
                                        </div>
                                        <div className="col-sm-9 col-8">
                                          {record.order_created_by
                                            ? `${record.order_created_by.first_name} ${record.order_created_by.last_name}`
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                      <div className="row">
                                        <div className="col-sm-3 col-4 text-secondary fw-medium">
                                          Created on:
                                        </div>
                                        <div className="col-sm-9 col-8">
                                          {record.created_at
                                            ? dayjs(record.created_at).format(
                                                "DD MMM YYYY, hh:mm A",
                                              )
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                                <div className="col-12 col-md-6">
                                  <div className="row">
                                    <div className="col-sm-3 col-4 text-secondary fw-medium">
                                      Lead Time:
                                    </div>
                                    <div className="col-sm-9 col-8">
                                      {(() => {
                                        const orderStatus = record?.order_status;
                                        const estimatedLeadTimeDays =
                                          record?.estimated_lead_time;
                                        const rawActualLeadTime =
                                          record?.actual_lead_time;

                                        if (
                                          estimatedLeadTimeDays == null &&
                                          rawActualLeadTime == null
                                        ) {
                                          return <span>-</span>;
                                        }

                                        const estimatedLeadTimeWeeks =
                                          estimatedLeadTimeDays != null
                                            ? Number(
                                                (
                                                  estimatedLeadTimeDays / 7
                                                ).toFixed(1),
                                              )
                                            : null;

                                        const actualLeadTimeWeeks =
                                          rawActualLeadTime != null
                                            ? Number(
                                                (
                                                  rawActualLeadTime / 7
                                                ).toFixed(1),
                                              )
                                            : 0;

                                        const isLeadTimeDispatched =
                                          [
                                            OrderStatusEnum.READY_FOR_DISPATCH,
                                            OrderStatusEnum.DISPATCHED,
                                            OrderStatusEnum.DELIVERED,
                                            OrderStatusEnum.UNDER_INSTALLATION,
                                            OrderStatusEnum.INSTALLED,
                                            OrderStatusEnum.CLOSED,
                                          ].includes(orderStatus) &&
                                          rawActualLeadTime != null;

                                        if (estimatedLeadTimeWeeks == null) {
                                          return <span>-</span>;
                                        }

                                        if (!isLeadTimeDispatched) {
                                          return (
                                            <span className="fw-semibold text-primary">
                                              {estimatedLeadTimeWeeks.toFixed(1)}{" "}
                                              {estimatedLeadTimeWeeks === 1
                                                ? "Week"
                                                : "Weeks"}
                                            </span>
                                          );
                                        }

                                        const diffWeeks = Number(
                                          (
                                            actualLeadTimeWeeks -
                                            estimatedLeadTimeWeeks
                                          ).toFixed(1),
                                        );
                                        const leadTimeExceeded = diffWeeks > 0;

                                        return (
                                          <div className="d-flex align-items-center gap-1 flex-wrap">
                                            <span className="fw-semibold text-primary">
                                              {estimatedLeadTimeWeeks.toFixed(1)} /{" "}
                                              {actualLeadTimeWeeks.toFixed(1)}{" "}
                                              Weeks
                                            </span>
                                            <span
                                              className={`fw-bold ${
                                                leadTimeExceeded
                                                  ? "text-danger"
                                                  : "text-success"
                                              }`}
                                            >
                                              ({leadTimeExceeded
                                                ? `+${diffWeeks}`
                                                : `${diffWeeks}`}{" "}
                                              {Math.abs(diffWeeks) === 1
                                                ? "Week"
                                                : "Weeks"})
                                            </span>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12" onClick={(e) => e.stopPropagation()}>
                                  <div className="d-flex align-items-center justify-content-between pt-1">
                                    <span className="text-secondary fw-medium">Accounts Approval:</span>
                                    {record?.accounts_approved == null ? (
                                      <span className="text-secondary">-</span>
                                    ) : record?.accounts_approved ? (
                                      <span className="accountsApprovalBadge approved">
                                        {svg.dotIcon}
                                        Approved
                                      </span>
                                    ) : (
                                      (() => {
                                        const canApprove = [
                                          ROLE.ADMIN,
                                          ROLE.SUB_ADMIN,
                                          ROLE.ACCOUNTS,
                                          ROLE.CREDIT_CONTROLLER
                                        ].includes(user?.role?.key);
                                        return (
                                          <span
                                            role={canApprove ? "button" : undefined}
                                            tabIndex={canApprove ? 0 : undefined}
                                            className={`accountsApprovalBadge needed ${!canApprove ? "non-clickable" : ""}`}
                                            style={!canApprove ? { cursor: "default", pointerEvents: "none" } : undefined}
                                            onClick={
                                              canApprove
                                                ? () => {
                                                    setSelectedOrderForApproval(record);
                                                    setAccountsApprovalModal(true);
                                                  }
                                                : undefined
                                            }
                                          >
                                            {svg.dotIcon}
                                            <span>
                                              Accounts Approval
                                              <br />
                                              Needed
                                            </span>
                                          </span>
                                        );
                                      })()
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Footer: Total Amount */}
                            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                              <span className="fw-bold">Amount:</span>
                              <span className="fw-bold fs-5">
                                {formatCurrency(
                                  record?.grand_total || 0,
                                  record?.currency,
                                  record?.conversion_rate,
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </InfiniteScroll>
              </>
            )}
          </div>
        </div>
      </div>

      {isCreateOrderModalOpen && (
        <CreateOrderModal
          open={isCreateOrderModalOpen}
          onClose={() => setIsCreateOrderModalOpen(false)}
          onSuccess={(ceatedData: any) => {
            navigate(`/orders/${ceatedData?._id}`);
          }}
          onAddressIncompleteError={(data: any) => {
            setIsCreateOrderModalOpen(false);
            setFailedAddressData(data?.data);
          }}
          onCustomerOnHoldError={(data: any) => {
            setIsCreateOrderModalOpen(false)
            setCustomerOnHoldModal(true);
            setCustomerOnHoldMessage(data)
          }}
        />
      )}
      {customerOnHoldModal && (
        <CustomerOnHoldWarningModal
          visible={customerOnHoldModal}
          onClose={() => setCustomerOnHoldModal(false)}
          customerOnHoldMessage={customerOnHoldMessage}
        />
      )}
      {isDeleteModal && (
        <CommonDeleteModal
          visible={isDeleteModal}
          setIsModalVisible={setIsDeleteModal}
          handleDelete={handleDeleteOrderData}
          title={"Remove Order, Are You Sure?"}
          description={
            "Are you sure you want to remove this order? All associated data will be permanently deleted."
          }
          setIsDeleteId={setIsDeleteId}
          isLoading={deleteOrderLoading}
          buttonName="REMOVE"
        />
      )}
      {archiveActionData && (
        <CommonConfirmationModal
          open={archiveActionData != null}
          title={
            archiveActionData?.type === "archive"
              ? "Archive Order, Are You Sure?"
              : "Unarchive Order, Are You Sure?"
          }
          subTitle={
            archiveActionData?.type === "archive"
              ? "Archiving this order will move it to the archived list. You can restore it later if needed."
              : "Unarchiving this order will restore it back to the active list."
          }
          loading={isTogglingArchive}
          onSuccess={handleArchiveConfirm}
          onClose={() => setArchiveActionData(null)}
          confirmButtonText={
            archiveActionData?.type === "archive" ? "ARCHIVE" : "UNARCHIVE"
          }
        />
      )}

      {failedAddressData !== null && (
        <ModifyDeliveryAddressModal
          open={failedAddressData !== null}
          onClose={() => setFailedAddressData(null)}
          addressData={failedAddressData?.customer_snapshot?.delivery_address}
          isDistributor={false}
          onSubmitOverride={(payload, setSaving) => {
            setSaving(true);
            dispatch(
              createOrderAPI({
                payload: payload,
                onSuccess: (data) => {
                  setSaving(false);
                  setFailedAddressData(null);
                  navigate(`/orders/${data?._id}`);
                },
                onError: () => {
                  setSaving(false);
                },
                onAddressIncompleteError: (data) => {
                  setFailedAddressData(data?.data);
                },
                onDrawingRequiredError: (data) => {
                  setDrawingRequiredData(data?.data || {});
                },
              }),
            );
          }}
          quoteData={failedAddressData}
        />
      )}

      {profitModal && (
        <ViewProfitDataModal
          visible={!!profitModal}
          setIsModalVisible={setProfitModal as any}
          onClose={() => {
            setProfitModal(false);
            setProfitData(null);
          }}
          profitData={profitData}
        />
      )}
      {drawingRequiredData !== null && (
        <ConfigureDrawingsModal
          open={drawingRequiredData !== null}
          onClose={() => setDrawingRequiredData(null)}
          drawingsData={drawingRequiredData?.variants_requiring_drawing}
          partsData={drawingRequiredData?.parts_requiring_drawing}
          componentsData={drawingRequiredData?.components_requiring_drawing}
          fullDrawingData={drawingRequiredData}
          quotationId={drawingRequiredData?.quote_id}
          onSubmit={(payload, setModalLoader) => {
            setModalLoader(true);
            dispatch(
              createOrderAPI({
                payload,
                onSuccess: (data) => {
                  setModalLoader(false);
                  setDrawingRequiredData(null);
                  navigate(`/orders/${data?._id}`);
                },
                onError: () => setModalLoader(false),
                onAddressIncompleteError: (data) => {
                  setModalLoader(false);
                  setDrawingRequiredData(null);
                  setFailedAddressData(data?.data);
                },
                onDrawingRequiredError: (data) => {
                  setDrawingRequiredData(data?.data || {});
                },
              }),
            );
          }}
        />
      )}
      {accountsApprovalModal && (
        <AccountsApprovalModal
          visible={accountsApprovalModal}
          onClose={() => {
            setAccountsApprovalModal(false);
            setSelectedOrderForApproval(null);
          }}
          orderData={selectedOrderForApproval}
          onSuccess={(data) => {
            const targetId = String(
              data?._id || selectedOrderForApproval?._id || "",
            );
            queryClient.setQueryData(["orders", queryFilters], (old: any) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page: any) => ({
                  ...page,
                  orders: (page.orders || []).map((o: any) =>
                    String(o._id) === targetId
                      ? {
                          ...o,
                          accounts_approved: true,
                          accounts_approved_by: data?.accounts_approved_by,
                          accounts_approved_at: data?.accounts_approved_at,
                        }
                      : o,
                  ),
                })),
              };
            });
            refetch();
          }}
        />
      )}
    </>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default Orders;
